import { identity, orderBy } from 'lodash-es'
import { invariant } from '#src/invariant.ts'

// A new derivation gets a new version. Codes from different versions never
// compare equal, so documents stamped under an older version only match again
// once a backfill restamps them.
export const DOCUMENT_FINGERPRINT_VERSION = 'v1'

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new Uint8Array(bytes).buffer
  )
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
}

// v1: SHA-256 over the newline-joined, sorted, lowercase-hex SHA-256 digests
// of each file's raw bytes. Sorting makes the same set of files match in any
// order; a file included twice still counts twice. Never hash decoded text:
// distinct binaries can decode to the same string.
export async function documentFingerprint(
  files: readonly Uint8Array[]
): Promise<string> {
  invariant(files.length > 0, 'A document fingerprint needs at least one file')
  const fileDigests = await Promise.all(files.map(sha256Hex))
  const sorted = orderBy(fileDigests, [identity], ['asc'])
  const digest = await sha256Hex(new TextEncoder().encode(sorted.join('\n')))
  return `${DOCUMENT_FINGERPRINT_VERSION}:${digest}`
}
